import { defineStore } from 'pinia'
import { io, Socket } from 'socket.io-client'
import config from '@/config'
import { useAuthStore } from '@/store/auth'
import { refreshToken } from '@/api/http'

type WebSocketMessage = {
  message_type?: string
  type?: string
  content?: any
  data?: any
}

export interface IAdminActivityStats {
  admin: string
  totalClicks: number
  perMinute: {
    timestamp: number
    count: number
  }[]
}

type ListenerCallback = (data: any) => void

export interface ArtefactProfitPayload {
  artefactProfitMap: Record<string, any>
  multiLotStrategies: Record<string, any>
}

const socketOptions = (token: string | null) => ({
  transports: ['websocket'] as Array<'websocket'>,
  autoConnect: false,
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 30000,
  randomizationFactor: 0.5,
  timeout: 20000,
  auth: {
    token
  }
})

const isAuthErrorMessage = (message?: string) =>
  /token|jwt|unauthori[sz]ed|forbidden/i.test(message || '')

export const useWebSocketStore = defineStore('websocket', {
  state: () => ({
    socket: null as Socket | null,
    artefactSocket: null as Socket | null,
    auctionData: [] as any[],
    priceStats: {
      hourly: [] as any[],
      daily: [] as any[],
      threeDays: [] as any[],
      weekly: [] as any[]
    },
    isConnected: false,
    isArtefactConnected: false,
    connectionError: null as string | null,
    processedLots: new Set<string>(),
    listeners: new Map<string, ListenerCallback>(),
    reconnectAttempts: 0,
    maxReconnectAttempts: Number.POSITIVE_INFINITY,
    reconnectDelay: 3000,
    reconnectTimer: null as number | null,
    isManualDisconnect: false,
    adminsOnline: [] as string[],
    adminActivityStats: [] as IAdminActivityStats[],
    isRefreshing: false,
    weaponModuleData: [] as any[],
    artefactAllMap: {} as Record<string, any>,
    artefactProfitData: {
      artefactProfitMap: {},
      multiLotStrategies: {}
    } as ArtefactProfitPayload,
    artefactProfitUpdatedAt: 0
  }),

  actions: {
    clearReconnectTimer() {
      if (this.reconnectTimer !== null) {
        window.clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
    },

    connect() {
      const auth = useAuthStore()

      if (!auth.accessToken) {
        this.connectionError = 'Missing access token'
        return
      }

      this.isManualDisconnect = false
      this.clearReconnectTimer()
      this.connectAuctionSocket(auth.accessToken)
      this.connectArtefactSocket(auth.accessToken)
    },

    connectAuctionSocket(token: string) {
      if (this.socket) {
        this.socket.auth = { token }

        if (!this.socket.connected) {
          this.socket.connect()
        }

        return
      }

      this.socket = io(`${config.wsUrl}/auction`, socketOptions(token))

      this.socket.on('connect', () => {
        this.isConnected = true
        this.connectionError = null
        this.reconnectAttempts = 0
        console.log('Socket.io /auction connected')
      })

      this.socket.on('disconnect', (reason: string) => {
        this.isConnected = false
        console.log('Socket.io /auction disconnected', reason)

        if (!this.isManualDisconnect && reason === 'io server disconnect') {
          this.handleReconnect()
        }
      })

      this.socket.on('connect_error', (error: Error) => {
        this.connectionError = error.message
        console.error('Socket.io /auction error:', error)

        if (isAuthErrorMessage(error.message)) {
          this.handleReconnect()
        }
      })

      this.socket.on('auth_error', async err => {
        if (err?.reason === 'TOKEN_EXPIRED' || err?.reason === 'INVALID_TOKEN') {
          await this.refreshAndReconnect()
        }
      })

      this.socket.on('message', (data: WebSocketMessage | string) => {
        this.handleIncomingMessage(data)
      })

      this.socket.on('auction_data', (data: any[]) => {
        this.auctionData = data
      })

      this.socket.on('initial_stats', (data: any) => {
        this.priceStats = {
          hourly: data.hourly || [],
          daily: data.daily || [],
          threeDays: data.threeDays || [],
          weekly: data.weekly || []
        }
      })

      this.socket.connect()
    },

    connectArtefactSocket(token: string) {
      if (this.artefactSocket) {
        this.artefactSocket.auth = { token }

        if (!this.artefactSocket.connected) {
          this.artefactSocket.connect()
        }

        return
      }

      this.artefactSocket = io(`${config.wsUrl}/artefacts`, socketOptions(token))

      this.artefactSocket.on('admins_online', (data: string[]) => {
        this.adminsOnline = data
      })

      this.artefactSocket.on('module_stats', (data: any[]) => {
        this.weaponModuleData = data
      })

      this.artefactSocket.on('artefact_all_map', (data: Record<string, any>) => {
        this.artefactAllMap = data
      })

      this.artefactSocket.on('artefact_profit_all', (data: ArtefactProfitPayload) => {
        this.artefactProfitData = {
          artefactProfitMap: data?.artefactProfitMap || {},
          multiLotStrategies: data?.multiLotStrategies || {}
        }
        this.artefactProfitUpdatedAt = Date.now()
      })

      this.artefactSocket.on('admin_activity_stats', (data: IAdminActivityStats[]) => {
        this.adminActivityStats = data
      })

      this.artefactSocket.on('connect', () => {
        this.isArtefactConnected = true
        this.connectionError = null
        this.reconnectAttempts = 0
        console.log('Socket.io /artefacts connected')
      })

      this.artefactSocket.on('disconnect', (reason: string) => {
        this.isArtefactConnected = false
        console.log('Socket.io /artefacts disconnected', reason)

        if (!this.isManualDisconnect && reason === 'io server disconnect') {
          this.handleReconnect()
        }
      })

      this.artefactSocket.on('connect_error', (error: Error) => {
        this.connectionError = error.message
        console.error('Socket.io /artefacts error:', error)

        if (isAuthErrorMessage(error.message)) {
          this.handleReconnect()
        }
      })

      this.artefactSocket.on('auth_error', async err => {
        if (err?.reason === 'TOKEN_EXPIRED' || err?.reason === 'INVALID_TOKEN') {
          await this.refreshAndReconnect()
        }
      })

      this.artefactSocket.on('connected', () => {
        console.log('Artefact socket received connected event')
      })

      this.artefactSocket.connect()
    },

    async refreshAndReconnect() {
      if (this.isRefreshing) return
      this.isRefreshing = true

      const authStore = useAuthStore()

      try {
        const refreshed = await refreshToken()

        if (!refreshed || !authStore.accessToken) {
          authStore.logout()
          this.disconnect()
          return
        }

        this.connect()
      } catch (e) {
        authStore.logout()
        this.disconnect()
      } finally {
        this.isRefreshing = false
      }
    },

    disconnect() {
      this.isManualDisconnect = true
      this.clearReconnectTimer()

      if (this.socket) {
        this.socket.removeAllListeners()
        this.socket.disconnect()
        this.socket = null
        this.isConnected = false
      }

      if (this.artefactSocket) {
        this.artefactSocket.removeAllListeners()
        this.artefactSocket.disconnect()
        this.artefactSocket = null
        this.isArtefactConnected = false
      }
    },

    handleReconnect() {
      const authStore = useAuthStore()

      if (!authStore.isAdmin) {
        console.warn('WebSocket: Connection refused - not admin')
        return
      }

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached')
        return
      }

      this.reconnectAttempts++
      const delay = Math.min(this.reconnectDelay * this.reconnectAttempts, 30000)

      this.clearReconnectTimer()
      this.reconnectTimer = window.setTimeout(() => {
        console.log(`Attempting reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`)
        this.reconnectTimer = null
        this.refreshAndReconnect()
      }, delay)
    },

    handleIncomingMessage(data: WebSocketMessage | string) {
      try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data

        if (parsed.message_type && this.listeners.has(parsed.message_type)) {
          this.listeners.get(parsed.message_type)?.(parsed.content || parsed)
        }

        if (parsed.type) {
          switch (parsed.type) {
            case 'auction_update':
              this.auctionData = parsed.data
              break
            case 'price_update':
              break
          }
        }
      } catch (error) {
        console.error('Message parsing error:', error)
      }
    },

    subscribe(messageType: string, callback: ListenerCallback) {
      this.listeners.set(messageType, callback)
    },

    unsubscribe(messageType: string) {
      this.listeners.delete(messageType)
    },

    send(data: any) {
      if (this.isConnected && this.socket) {
        this.socket.emit('message', JSON.stringify(data))
      } else {
        console.error('WebSocket is not connected')
      }
    },

    sendArt(data: any) {
      if (this.isArtefactConnected && this.artefactSocket) {
        this.artefactSocket.emit('click', JSON.stringify(data))
      } else {
        console.error('WebSocket is not connected')
      }
    }
  },

  getters: {
    getAuctionData: state => state.auctionData,
    getPriceStats: state => (period: keyof typeof state.priceStats) => state.priceStats[period],
    getConnectionStatus: state => state.isConnected,
    getArtefactConnectionStatus: state => state.isArtefactConnected,
    getConnectionError: state => state.connectionError
  }
})
